import { FloatingFormItem, InputDecimal } from '@/components/_common';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Statistic,
  Tooltip,
} from 'antd';
import { useEffect, useMemo } from 'react';
import { HelpCircle } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WorkOrderItemFormData } from '../WorkOrderItemPartForm';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import Authorities from '@/types/Authority';
import SupplierSelect from '@/components/Suppliers/SupplierSelect';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import { useWorkorderItemPermissions } from '../WorkOrderItemPartForm/workorderItemPermissions';
import fieldRules from '@/utils/functions/fieldRules';
import EmployeesByCompanySelectField from '@/components/_common/EmployeesByCompanySelectField';
import { WorkOrderItem } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';
import { useCustomerGeneral } from '@/core/domain/people/Customers/Customers.hooks';
import TextArea from 'antd/lib/input/TextArea';
import { WorkorderItem } from '../WorkOrderItemModal';

const defaultStatistcProps = {
  decimalSeparator: ',',
  groupSeparator: '.',
};

type Props = {
  form: FormInstance;
  onValuesChange?: (field: unknown, values: WorkOrderItemFormData) => void;
  isEdit?: boolean;
  workorderItem?: WorkOrderItem;
  currentItem?: WorkorderItem;
};

const WorkOrderItemServiceForm: React.FC<Props> = ({
  form,
  onValuesChange,
  isEdit = false,
  workorderItem,
  currentItem,
}) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const quantity = Form.useWatch(['quantity'], form);
  const unitPrice = Form.useWatch(['unitPrice'], form);
  const unitCost = Form.useWatch(['unitCost'], form);
  const discountValue = Form.useWatch(['discountValue'], form);
  const isInternal = Form.useWatch(['isInternal'], form);

  const subTotal = (quantity || 0) * (unitPrice || 0);
  const amount = subTotal - discountValue;

  const itemsPermission = useWorkorderItemPermissions();

  const hasDiscountAboveDefault = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OVER_LIMIT,
  ]);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const hasPolicyAndBenefit =
    !!complement?.policyNumber && !!workorderItem?.benefitId;

  const { data: companyWorkorderConfig, isLoading: configIsLoading } =
    useWorkorderConfigurationCompanyQuery(workorder?.company.id);

  const { data: customer } = useCustomerGeneral(workorder?.customer?.id);

  const minDiscountPolicy = useMemo(() => {
    if (
      isAppPortocar &&
      !workorder?.partnerDiscount &&
      workorder?.employeeDiscount
    )
      return 10;

    if (
      !isAppPortocar ||
      !complement?.policyNumber ||
      !workorder?.partnerDiscount
    )
      return undefined;

    const isPortoPolicy =
      !!complement?.policyNumber &&
      complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

    return isPortoPolicy ? 20 : 15;
  }, [
    isAppPortocar,
    complement?.policyNumber,
    complement?.companyCode,
    workorderItem,
    workorder?.partnerDiscount,
  ]);

  useEffect(() => {
    if (companyWorkorderConfig) {
      form.setFieldValue(
        'configDiscount',
        (companyWorkorderConfig.maxServiceDiscount || 0) * 100,
      );
    }
  }, []);

  return (
    <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
      {!itemsPermission.addDiscountGreaterDefinedByCompany &&
        !companyWorkorderConfig?.maxServiceDiscount &&
        !configIsLoading && (
          <Alert
            type="warning"
            description="Não há configuração de desconto de serviço a empresa relacionada."
            style={{ marginBottom: 24 }}
          />
        )}

      <Form.Item hidden name="type">
        <Input />
      </Form.Item>
      <Form.Item hidden name="configDiscount" initialValue={0}>
        <Input />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col span={24}>
          <FloatingFormItem
            label="Descrição"
            name="itemName"
            rules={[
              defaultEmptyFieldError,
              { type: 'string', max: 120, message: 'Máximo de 120 caracteres' },
            ]}
          >
            <Input
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_DESCRIPTION,
                ]) ||
                hasPolicyAndBenefit ||
                !!workorderItem?.portoTaskId
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Área"
            name="subAreaId"
            rules={[defaultEmptyFieldError]}
          >
            <AreasInfiniteSelectField
              subAreaSelected={workorderItem?.subArea || currentItem?.subArea}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Tempo"
            name="quantity"
            rules={[defaultEmptyFieldError]}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const unitPrice = form.getFieldValue('unitPrice');

              const discountValue = (value * unitPrice * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
          >
            <InputDecimal
              disabled={
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_QUANTITY,
                  ])) ||
                (isEdit &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_QUANTITY,
                  ]))
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Preço Unit."
            name="unitPrice"
            initialValue={0}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const quantity = form.getFieldValue('quantity');

              const discountValue = (value * quantity * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
            rules={fieldRules([
              {
                enabled: !itemsPermission.allowSellingBellowCostValue,
                type: 'number',
                min: unitCost,
                message: `Valor mínimo de ${formatterPrice(unitCost)}`,
              },
              {
                enabled:
                  !itemsPermission.makeSaleAboveCreditLimit &&
                  typeof customer?.creditLimit === 'number',
                type: 'number',
                max: customer?.creditLimit,
                message: `Limite de crédito de: ${formatterPrice(
                  customer?.creditLimit || 0,
                )}`,
              },
            ])}
          >
            <InputDecimal
              disabled={
                hasPolicyAndBenefit ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_VALUE,
                  ])) ||
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_SERVICE_EDIT_SALE_VALUE,
                ])
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Desconto (%)"
            name="discount"
            initialValue={0}
            getValueFromEvent={(value) => {
              const calc = ((subTotal || 0) * (value || 0)) / 100;

              form.setFieldValue('discountValue', calc);

              return value;
            }}
            rules={[
              () => ({
                max:
                  !itemsPermission.addDiscountGreaterDefinedByCompany &&
                  !hasDiscountAboveDefault
                    ? (companyWorkorderConfig?.maxServiceDiscount ?? 0) * 100
                    : undefined,
                type: 'number',
                message: `Desconto não pode ser maior que ${formatterPercent(
                  companyWorkorderConfig?.maxServiceDiscount || 0,
                )}`,
              }),
              hasPolicyAndBenefit
                ? { type: 'number' }
                : {
                    min: minDiscountPolicy,
                    type: 'number',
                    message: `Desconto não pode ser menor que ${formatterPercent(
                      (minDiscountPolicy || 0) / 100,
                    )}`,
                  },
            ]}
          >
            <InputDecimal
              min={0}
              max={100}
              disabled={
                !unitPrice ||
                (!itemsPermission.addDiscountGreaterDefinedByCompany &&
                  typeof companyWorkorderConfig?.maxServiceDiscount !==
                    'number') ||
                (isInternal === 'false' &&
                  !itemsPermission.giveDiscountOutsourcedServices)
              }
              key={companyWorkorderConfig?.company.id}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Desconto (R$)"
            name="discountValue"
            initialValue={0}
            getValueFromEvent={(value) => {
              const calc = (100 * (value || 0)) / (subTotal || 0);

              form.setFieldValue('discount', calc);

              return value;
            }}
            rules={[
              () => {
                const permission =
                  !itemsPermission.addDiscountGreaterDefinedByCompany &&
                  !hasDiscountAboveDefault;
                const maxDiscount =
                  subTotal * Number(companyWorkorderConfig?.maxServiceDiscount);

                return {
                  max: permission && maxDiscount ? maxDiscount : undefined,
                  type: 'number',
                  message: `Desconto não pode ser maior que ${formatterPrice(
                    maxDiscount,
                  )}`,
                };
              },
            ]}
          >
            <InputDecimal
              min={0}
              max={subTotal || 0}
              disabled={
                !unitPrice ||
                (!itemsPermission.addDiscountGreaterDefinedByCompany &&
                  !companyWorkorderConfig?.maxServiceDiscount) ||
                (isInternal === 'false' &&
                  !itemsPermission.giveDiscountOutsourcedServices)
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Preço de custo (Unit.)"
            name="unitCost"
            initialValue={0}
            rules={[
              {
                type: 'number',
                required: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ]),
                min: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ])
                  ? 1
                  : 0,
              },
            ]}
          >
            <InputDecimal
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_EDIT_COST_PRICE,
                ]) ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_COST_PRICE,
                  ]))
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={10}>
          <FloatingFormItem
            name="employeeId"
            label="Funcionário"
            rules={
              !hasPermissions([
                Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_EMPLOYEE,
              ])
                ? [defaultEmptyFieldError]
                : []
            }
          >
            <EmployeesByCompanySelectField companyId={workorder?.company.id} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <Form.Item
            name="isPaintedPart"
            initialValue="false"
            label={
              <span style={{ display: 'inline-flex' }}>
                Contar como peça pintada
                <Tooltip overlay="Comissão por peça pintada">
                  <AntdFeatherIcon
                    icon={<HelpCircle style={{ marginLeft: 5 }} />}
                    size={16}
                  />
                </Tooltip>
              </span>
            }
          >
            <Radio.Group>
              <Radio value="true">
                <FormattedMessage id="commons.options.yes" />
              </Radio>
              <Radio value="false">
                <FormattedMessage id="commons.options.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={24} md={12} lg={6}>
          <Form.Item
            name="isInternal"
            initialValue="true"
            label="Serviço terceirizado?"
          >
            <Radio.Group>
              <Radio value="false">
                <FormattedMessage id="commons.options.yes" />
              </Radio>

              <Radio value="true">
                <FormattedMessage id="commons.options.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {!isEdit && (
          <Col span={24}>
            <FloatingFormItem name="observation" label="Observação">
              <TextArea disabled rows={2} />
            </FloatingFormItem>
          </Col>
        )}

        {!JSON.parse(isInternal ?? false) && (
          <Col span={24} md={12} lg={10}>
            <FloatingFormItem
              name="supplierId"
              label="Fornecedor"
              rules={[
                {
                  required: !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_SUPPLIER,
                  ]),
                },
              ]}
            >
              <SupplierSelect />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24}>
          <Statistic
            {...defaultStatistcProps}
            title={
              <FormattedMessage id="workOrders.details.items.serviceTotal" />
            }
            value={quantityFormatter(amount)}
            prefix="R$"
            valueStyle={{ color: '#364068' }}
            style={{ float: 'right' }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default WorkOrderItemServiceForm;
